import { clientMoveIn } from "../../lib/clientMoveIn";
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PhoneIcon from "@mui/icons-material/Phone";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonIcon from "@mui/icons-material/Person";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AdjustIcon from "@mui/icons-material/Adjust";

import { supabase } from "../../lib/supabase";
import { changeClientStatus } from "../clients/clientMutations";
import { useMarkInvoicePaidPrompt } from "../clients/MarkInvoicePaidDialog";
import {
  pipelineStatuses,
  shortlistActionLabel,
  statusActions,
  statusConfig,
  statusPrimaryKeys,
} from "../clients/config";
import { useShortlistHasItems } from "../../hooks/useShortlistHasItems";
import type { Client, ClientStatus } from "../clients/types";

// TRAIN-2b groundwork · the client-context rail for the Messages surface.
//
// The Slack anatomy: conversation in the middle, the PERSON on the right. This
// rail is the client card unbundled — the facts you need mid-sentence (budget,
// move-in, stage) plus the stage-keyed next steps, without leaving the thread.
//
// Deliberate reuse over invention: pipeline order, stage labels/colors and the
// stage->actions mapping all come from components/clients/config.tsx — the
// SAME config the DetailDrawer's action row uses. If a stage gains an action
// there, this rail inherits it. A second copy would drift; the drawer and the
// rail disagreeing about "what's next for Applied" is exactly the kind of lie
// this estate spent a chapter killing.
//
// GROUNDWORK SCOPE, stated so nobody mistakes the edges:
//   · facts + pipeline + stage actions render from the live client row
//   · tapping a pipeline stage MOVES the client (same updateClientById path
//     the client list uses; migration 060's trigger writes the history row)
//   · stage ACTION buttons run in place through `onAction` (the page mounts
//     the same dialogs Leads uses — PKT-0866 guest card, PKT-0873 the rest);
//     an action the page does not handle falls back to the full record
//   · timeline-in-thread (system events between bubbles) is NOT this change

export interface ClientContextRailProps {
  clientId: string;
  /**
   * Ch78 · texting consent for this client, when the page already knows it
   * (the queue row carries it; one batched read). "unknown" or absent renders
   * nothing here — the thread panel owns the "Record consent" banner.
   */
  consentState?: "granted" | "denied" | "unknown" | null;
  /** HOW they consented (ledger source), when known. */
  consentSource?: string | null;
  /** Collapse toggle lives with the parent so the layout can reclaim width. */
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onOpenClientRecord: (clientId: string, tab?: string) => void;
  /** Optional: notify the page after a successful stage change so the
   * conversation list can refresh its ordering/badges. */
  onClientChanged?: () => void;
  /**
   * Execute a next-step action IN PLACE.
   *
   * Before this existed every button under "Next steps" called
   * openFullRecord("timeline") — the rail advertised four actions and
   * performed none of them, navigating away from the thread instead. That is
   * the difference between an action panel and a table of contents.
   *
   * The parent owns execution because the modals (ShortlistSearchModal,
   * GuestCardDialog, SendInvoiceModal, UploadLeaseModal, LogActivityDialog)
   * and the composer belong to the page, not the rail.
   * Return false to fall back to opening the full record, so an unhandled
   * action degrades to the old behaviour rather than doing nothing.
   */
  onAction?: (actionKey: string) => boolean | void;
  /** Bump after the shortlist modal saves so Create List becomes Edit List. */
  shortlistRefreshKey?: number;
  /**
   * "side" (default) is the desktop column. "sheet" fills a mobile drawer:
   * full width, and the collapse chevron becomes the drawer's Close X. Same
   * component either way — the phone gets THIS rail, not a copy.
   */
  variant?: "side" | "sheet";
}

const RAIL_WIDTH = 264;

function formatBudget(client: Client): string {
  const v = client.max_budget;
  if (v === null || v === undefined) return "—";
  return `$${Number(v).toLocaleString()}`;
}

function formatMoveIn(client: Client): string {
  return clientMoveIn(client) || "—";
}

/** "2h ago" / "Aug 4" — the triage fact, not a full timestamp. */
function formatLastContact(raw: unknown): string | null {
  if (!raw || typeof raw !== "string") return null;
  const then = new Date(raw);
  if (isNaN(then.getTime())) return null;
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return then.toLocaleDateString([], { month: "short", day: "numeric" });
}

export default function ClientContextRail({
  clientId,
  consentState,
  consentSource,
  collapsed,
  onToggleCollapsed,
  onOpenClientRecord,
  onClientChanged,
  onAction,
  shortlistRefreshKey = 0,
  variant = "side",
}: ClientContextRailProps) {
  const sheet = variant === "sheet";
  const shortlistHasItems = useShortlistHasItems(clientId, shortlistRefreshKey);
  const [client, setClient] = useState<Client | null>(null);
  const [agentName, setAgentName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingStatus, setSavingStatus] = useState<ClientStatus | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // PKT-0916: after a move INTO Payment Received, offer to mark an open
  // invoice paid (same backend route as the invoice modal).
  const markPaid = useMarkInvoicePaidPrompt(() => onClientChanged?.());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase
        .from("clients")
        .select(
          "id,first_name,last_name,email,phone,status,client_type,max_budget," +
            "move_in_date,move_in_timeline,assigned_agent_id,last_contacted_at"
        )
        .eq("id", clientId)
        .maybeSingle();
      if (dbError) throw dbError;
      const row = (data as unknown as Client | null) ?? null;
      setClient(row);
      if (row?.assigned_agent_id) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("full_name,email")
          .eq("id", row.assigned_agent_id)
          .maybeSingle();
        const p = profile as unknown as { full_name?: string; email?: string } | null;
        setAgentName(p?.full_name || p?.email || null);
      } else {
        setAgentName(null);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load client");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const moveToStatus = async (next: ClientStatus) => {
    if (!client || savingStatus || next === client.status) return;
    setSavingStatus(next);
    setError(null);
    setNotice(null);
    try {
      // ONE writer, shared with the Clients list. A reviewer found this rail
      // wrote only the UPDATE while the list also wrote an activity and a
      // notification — the same move producing different history depending on
      // which screen fired it. See changeClientStatus for the full note.
      const {
        data: { user },
      } = await supabase.auth.getUser();
      // Symmetry with ClientList, which already guards this. Proceeding with a
      // NULL actor writes an activity row that `activities`' own
      // `WITH CHECK (auth.uid() = user_id)` rejects, so the stage would move
      // with a silently missing audit entry — the exact failure this PR exists
      // to end. Degenerate in practice (no session, no mutation), but an
      // asymmetry in a change whose entire thesis is symmetry.
      if (!user) return;
      const outcome = await changeClientStatus(supabase, {
        clientId: client.id,
        clientName: `${client.first_name} ${client.last_name}`.trim(),
        previousStatus: client.status,
        nextStatus: next,
        labelFor: (st) => statusConfig[st as ClientStatus]?.label || st,
        actorUserId: user.id,
      });
      if (outcome.error) throw outcome.error;
      setClient((prev) => (prev ? { ...prev, status: next } : prev));
      onClientChanged?.();
      if (next === "payment_received") {
        void markPaid.offer(client.id, `${client.first_name} ${client.last_name}`.trim());
      }
      if (!outcome.sideEffectsWritten) {
        // The move happened. Say what did NOT, rather than implying failure.
        setNotice("Stage moved — timeline entry could not be written.");
      }
    } catch (err: any) {
      // The write failed — the UI must not claim the move happened.
      setError(err?.message || "Could not change status");
    } finally {
      setSavingStatus(null);
    }
  };

  /** Deep-link into the full client drawer using the estate's existing URL
   * navigation (the TRAIN-3 machinery — page + client params survive cold
   * loads). Groundwork: the drawer owns the actual flows. */
  const openFullRecord = (tab?: string) => {
    onOpenClientRecord(clientId, tab);
  };

  if (collapsed) {
    return (
      <Box
        data-testid="client-rail-collapsed"
        sx={{
          width: 40,
          flexShrink: 0,
          borderLeft: (t) => `1px solid ${t.palette.divider}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 1,
        }}
      >
        <Tooltip title="Show client details">
          <IconButton
            size="small"
            data-testid="client-rail-expand"
            onClick={onToggleCollapsed}
            aria-label="Show client details"
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      data-testid="client-rail"
      sx={{
        width: sheet ? "100%" : RAIL_WIDTH,
        flexShrink: 0,
        ...(sheet ? { height: "100%", pt: "calc(env(safe-area-inset-top, 0px) + 12px)" } : {
          borderLeft: (t: any) => `1px solid ${t.palette.divider}`,
        }),
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        p: 1.5,
        gap: 1.25,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          variant="overline"
          sx={{ color: "text.secondary", letterSpacing: 0.6, lineHeight: 1 }}
        >
          Client
        </Typography>
        <Tooltip title={sheet ? "Close" : "Hide client details"}>
          <IconButton
            size="small"
            data-testid={sheet ? "client-rail-close" : "client-rail-collapse"}
            onClick={onToggleCollapsed}
            aria-label={sheet ? "Close client details" : "Hide client details"}
          >
            {sheet ? <CloseIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={20} data-testid="client-rail-loading" />
        </Box>
      ) : !client ? (
        <Alert
          severity="info"
          data-testid="client-rail-missing"
          action={
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={load}
              data-testid="client-rail-retry"
              sx={{ textTransform: "none" }}
            >
              Retry
            </Button>
          }
        >
          {error || "Client not found."}
        </Alert>
      ) : (
        <>
          <Box>
            <Typography variant="subtitle2" data-testid="client-rail-name">
              {client.first_name} {client.last_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {statusConfig[client.status]?.label || client.status}
              {client.client_type ? ` · ${client.client_type}` : ""}
            </Typography>
            {/* Ch78 · the first fact about a person you are about to text. */}
            {consentState === "granted" && (
              <Typography
                variant="caption"
                data-testid="client-rail-texts"
                sx={{ display: "block", color: "success.main", fontWeight: 700 }}
              >
                {consentSource === "website_form"
                  ? "Texts OK · asked on the form"
                  : "Texts OK · consent on file"}
              </Typography>
            )}
            {consentState === "denied" && (
              <Typography
                variant="caption"
                data-testid="client-rail-texts"
                sx={{ display: "block", color: "error.main", fontWeight: 700 }}
              >
                No texts · STOP on record
              </Typography>
            )}
            {/* The query already fetched phone + email and rendered neither.
                A locator standing at a property needs to call from the thread,
                not navigate to the client record to find the number. */}
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
              {client.phone && (
                <Tooltip title={`Call ${client.phone}`}>
                  <IconButton
                    size="small"
                    component="a"
                    href={`tel:${String(client.phone).replace(/[^\d+]/g, "")}`}
                    data-testid="client-rail-call"
                    aria-label={`Call ${client.phone}`}
                    sx={{ border: 1, borderColor: "divider", borderRadius: 1.5 }}
                  >
                    <PhoneIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              {client.email && (
                <Tooltip title={client.email}>
                  <IconButton
                    size="small"
                    component="a"
                    href={`mailto:${client.email}`}
                    data-testid="client-rail-email"
                    aria-label={`Email ${client.email}`}
                    sx={{ border: 1, borderColor: "divider", borderRadius: 1.5 }}
                  >
                    <MailOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Box>

          <Stack direction="row" spacing={1}>
            <Box
              sx={{
                flex: 1,
                bgcolor: "action.hover",
                borderRadius: 1.5,
                px: 1.25,
                py: 1,
              }}
              data-testid="client-rail-budget"
            >
              <Typography variant="caption" color="text.secondary" display="block">
                Budget
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {formatBudget(client)}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                bgcolor: "action.hover",
                borderRadius: 1.5,
                px: 1.25,
                py: 1,
              }}
              data-testid="client-rail-movein"
            >
              <Typography variant="caption" color="text.secondary" display="block">
                Move-in
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {formatMoveIn(client)}
              </Typography>
            </Box>
          </Stack>

          <Box data-testid="client-rail-pipeline">
            <Typography
              variant="overline"
              sx={{ color: "text.secondary", letterSpacing: 0.6, lineHeight: 1 }}
            >
              Pipeline
            </Typography>
            <Stack sx={{ mt: 0.5 }} spacing={0}>
              {pipelineStatuses.map((stage, i) => {
                const reached =
                  pipelineStatuses.indexOf(client.status) >= i &&
                  pipelineStatuses.includes(client.status);
                const current = client.status === stage;
                const saving = savingStatus === stage;
                return (
                  <React.Fragment key={stage}>
                    {i > 0 && (
                      <Box
                        sx={{
                          width: "2px",
                          height: 10,
                          bgcolor: reached ? "success.main" : "divider",
                          opacity: reached ? 0.5 : 1,
                          ml: "9px",
                          borderRadius: 1,
                        }}
                      />
                    )}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      component="button"
                      data-testid={`client-rail-stage-${stage}`}
                      aria-current={current ? "step" : undefined}
                      onClick={() => moveToStatus(stage)}
                      disabled={!!savingStatus}
                      sx={{
                        border: "none",
                        background: "none",
                        px: 0.75,
                        py: 0.5,
                        ml: -0.75,
                        cursor: current ? "default" : "pointer",
                        textAlign: "left",
                        borderRadius: 1.5,
                        transition: "background-color 120ms ease",
                        "&:hover": current ? {} : { bgcolor: "action.hover" },
                      }}
                    >
                      {saving ? (
                        <CircularProgress size={14} />
                      ) : current ? (
                        <AdjustIcon sx={{ fontSize: 16, color: "primary.main" }} />
                      ) : reached ? (
                        <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
                      ) : (
                        <RadioButtonUncheckedIcon
                          sx={{ fontSize: 16, color: "text.disabled" }}
                        />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: current ? 500 : 400,
                          color: current
                            ? "primary.main"
                            : reached
                            ? "text.secondary"
                            : "text.disabled",
                        }}
                      >
                        {statusConfig[stage]?.label || stage}
                      </Typography>
                    </Stack>
                  </React.Fragment>
                );
              })}
            </Stack>
          </Box>

          <Divider />

          <Box data-testid="client-rail-actions">
            <Typography
              variant="overline"
              sx={{ color: "text.secondary", letterSpacing: 0.6, lineHeight: 1 }}
            >
              Next steps
            </Typography>
            <Stack spacing={0.75} sx={{ mt: 0.5 }}>
              {/* Grant's ruling mid-build: the guest card rides EVERY stage —
                  it is the daily-driver artifact, not a stage step. Sourced
                  from the touring stage's config entry so there is still only
                  ONE definition of it; more drawer actions migrate here in the
                  carry-over phase. */}
              {[
                ...(statusActions[client.status] || []),
                ...(statusActions[client.status] || []).some(
                  (a) => a.key === "send_guest_card"
                )
                  ? []
                  : (statusActions.touring || []).filter(
                      (a) => a.key === "send_guest_card"
                    ),
              ].map((action, i, all) => {
                // The filled button is the stage's emphasised action, not
                // whatever sits first (PKT-0841: one shared list, same emphasis).
                const primaryKey = statusPrimaryKeys[client.status]?.[0] ?? all[0]?.key;
                const label = action.key === "send_list" ? shortlistActionLabel(shortlistHasItems) : action.label;
                return (
                <Button
                  key={action.key}
                  size="small"
                  /* ONE declared next action, not four equal buttons. Grant's
                     own ruling on the content lane applies here: optimise for
                     the button that actually gets pressed, not the one that
                     looks tidiest in a row. */
                  variant={action.key === primaryKey ? "contained" : "outlined"}
                  disableElevation
                  startIcon={action.icon}
                  data-testid={`client-rail-action-${action.key}`}
                  onClick={() => {
                    // Execute in place. Fall back to the old navigate-away only
                    // when the parent does not handle this action, so an
                    // unwired key degrades instead of silently doing nothing.
                    const handled = onAction?.(action.key);
                    if (handled === false || handled === undefined) {
                      openFullRecord("timeline");
                    }
                  }}
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    borderRadius: 1.5,
                    py: 0.75,
                    ...(action.key === primaryKey
                      ? {}
                      : {
                          color: "text.primary",
                          borderColor: "divider",
                          "&:hover": {
                            borderColor: "text.disabled",
                            bgcolor: "action.hover",
                          },
                        }),
                  }}
                >
                  {label}
                </Button>
                );
              })}
            </Stack>
          </Box>

          {error && (
            <Alert severity="error" data-testid="client-rail-error">
              {error}
            </Alert>
          )}

          {notice && (
            <Alert severity="warning" data-testid="client-rail-notice">
              {notice}
            </Alert>
          )}

          <Box sx={{ mt: "auto", pt: 1 }}>
            {agentName && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
                data-testid="client-rail-agent"
              >
                <PersonIcon sx={{ fontSize: 14 }} /> Agent · {agentName}
              </Typography>
            )}
            {formatLastContact((client as any).last_contacted_at) && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
                data-testid="client-rail-last-contact"
              >
                <ScheduleIcon sx={{ fontSize: 14 }} /> Last contact ·{" "}
                {formatLastContact((client as any).last_contacted_at)}
              </Typography>
            )}
            <Button
              size="small"
              startIcon={<OpenInNewIcon />}
              data-testid="client-rail-open-full"
              onClick={() => openFullRecord()}
              sx={{ textTransform: "none", px: 0.5 }}
            >
              Open full client record
            </Button>
          </Box>
        </>
      )}
      {markPaid.dialog}
    </Box>
  );
}
